import { useEffect, useState, useMemo, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Divider, Typography } from '@mui/material';
import { AddCircleOutline as AddIcon } from '@mui/icons-material';

import Assessment from './components/Assessment/Assessment';
import AssessmentReview from './components/AssessmentReview/AssessmentReview';
import AssessmentSummary from './components/AssessmentSummary/AssessmentSummary';
import { FBoxContainer, FHButton, FProgressBar } from '../../../base';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import {
  assessmentActions,
  maladaptiveBehaviorActions,
  treatmentPlanActions,
} from '../../../../store';
import { validateInitialAssesmentsTools } from './utils/validations';
import { getClientAge, getMissingRecommendedToolsText } from './utils/general';
import { Themes } from '../../../../constants';
import { TreatmentPlanPages } from '../constants/general';
import { ERROR_STATES, PERSIST_DATA_STATES, STATE_IDS } from './constants/states';
import { FORTA_ERROR, FORTA_BLUE, FORTA_WHITE, FORTA_ORANGE } from '../../../../utils/colors';
import ErrorIcon from '../../../../assets/images/error-alert.svg';

import type { IAssessmentTool } from '../../../../utils/types';

function InitialAssessment() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const {
    assessment,
    tools,
    loading: assessmentLoading,
    toolsLoading,
    barriersLoading,
    errorState,
    persistDataState,
    treatmentPlanId: currentTPId,
  } = useSelector((state: any) => state.assessments);
  const assessmentVersion = treatmentPlan?.resource_versions?.assessment;
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [toolCount, setToolCount] = useState<number>(0);

  const planId = treatmentPlan?.id;
  const isEditable = useTreatmentPlanEditable();

  const clientAge = useMemo(
    () => getClientAge(treatmentPlan?.client?.dob, assessment?.date_of_assessment),
    [treatmentPlan?.client?.dob, assessment?.date_of_assessment],
  );
  const { assessmentMethodsErrorCount, missingRecommendedTools } = useMemo(
    () =>
      validateInitialAssesmentsTools(
        assessment,
        tools,
        persistDataState,
        assessmentVersion,
        clientAge,
      ),
    [assessment, tools, persistDataState, assessmentVersion, clientAge],
  );
  const missingRecommendedToolsText = useMemo(
    () => getMissingRecommendedToolsText(missingRecommendedTools),
    [missingRecommendedTools],
  );

  useEffect(() => {
    if (!treatmentPlan?.id) return;
    dispatch(assessmentActions.updateState({ key: 'treatmentPlanId', data: treatmentPlan?.id }));
    if (currentTPId !== treatmentPlan.id) {
      dispatch(assessmentActions.updateState({ key: 'persistDataState', data: {} }));
      dispatch(assessmentActions.updateState({ key: 'errorState', data: {} }));
    }
    fetchInitialAssessmentData(treatmentPlan.id);
  }, [treatmentPlan]);

  useEffect(() => {
    let toolLengthErrorValue = 0;
    if (tools.length > toolCount && isAdding) {
      setIsAdding(false);
    }
    if (tools.length === 0 && planId && assessment?.id) {
      toolLengthErrorValue = 1;
    }

    if (persistDataState[`${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-new`]) {
      setIsAdding(true);
      toolLengthErrorValue = 0;
    } else {
      setIsAdding(false);
      dispatch(
        assessmentActions.updateErrorState({
          data: {
            [`${ERROR_STATES[STATE_IDS.ASSESSMENT_METHOD]}-new`]: 0,
          },
        }),
      );
    }

    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [ERROR_STATES[STATE_IDS.NO_ASSESSMENT_TOOL]]: toolLengthErrorValue,
        },
      }),
    );
    setToolCount(tools.length);
  }, [tools, assessment]);

  useEffect(() => {
    let count = 0;
    if (!errorState) {
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.Assessment]: count,
        }),
      );
      return;
    }
    for (const key in errorState) {
      count += errorState[key];
    }
    dispatch(
      treatmentPlanActions.updateBadge({
        [TreatmentPlanPages.Assessment]: count,
      }),
    );
  }, [errorState]);

  const fetchInitialAssessmentData = async (planId: number) => {
    dispatch(maladaptiveBehaviorActions.getMaladaptiveBehavior({ getAccessTokenSilently, planId }));
    const { payload: assessment } = await dispatch(
      assessmentActions.getAssessment({ getAccessTokenSilently, planId }),
    );
    if (!assessment?.id) {
      dispatch(assessmentActions.clearState());
      return;
    }
    dispatch(
      assessmentActions.getBarriers({
        getAccessTokenSilently,
        planId,
        assessmentId: assessment.id,
      }),
    );
    const { payload: tools } = await dispatch(
      assessmentActions.getTools({ getAccessTokenSilently, planId, assessmentId: assessment.id }),
    );
    tools.forEach((tool: IAssessmentTool) => {
      dispatch(
        assessmentActions.getToolDocuments({
          getAccessTokenSilently,
          planId,
          assessmentId: assessment.id,
          toolId: tool.id,
        }),
      );
    });
  };

  const handleAddAssessmentTool = () => {
    setIsAdding(true);
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [ERROR_STATES[STATE_IDS.NO_ASSESSMENT_TOOL]]: 0,
        },
      }),
    );
  };

  const handleDeleteNewAssessment = () => {
    setIsAdding(false);
    const errorStateUpdate = {
      data: {
        [`${ERROR_STATES[STATE_IDS.ASSESSMENT_METHOD]}-new`]: 0,
      },
    };
    if (tools.length === 0) {
      errorStateUpdate.data[ERROR_STATES[STATE_IDS.NO_ASSESSMENT_TOOL]] = 1;
    }
    dispatch(assessmentActions.updateErrorState(errorStateUpdate));
    dispatch(
      assessmentActions.updatePersistDataState({
        data: {
          [`${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-new`]: null,
        },
      }),
    );
  };

  return (
    <Box>
      {planId && (
        <>
          <AssessmentReview />
          <FBoxContainer colorTheme={Themes.Primary} title="Assessment methods" sx={{ mt: 3 }}>
            {isEditable && Boolean(assessment?.id) && (
              <>
                <Typography
                  textAlign={'left'}
                  alignSelf="start"
                  fontWeight={'700'}
                  lineHeight={'21px'}
                  marginLeft={'0px'}
                  mb={3}
                  sx={{
                    color: assessmentMethodsErrorCount > 0 ? FORTA_ERROR : FORTA_BLUE.light1,
                  }}
                >
                  {assessmentMethodsErrorCount > 0 && (
                    <img
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '8px',
                        marginTop: '-4px',
                      }}
                      src={ErrorIcon}
                      alt="attention-icon"
                    />
                  )}
                  Attention: All fields with * are required to be filled
                </Typography>
                {missingRecommendedTools.length > 0 && (
                  <Typography
                    textAlign={'left'}
                    alignSelf="start"
                    fontWeight={'700'}
                    lineHeight={'21px'}
                    marginLeft={'0px'}
                    mb={3}
                    sx={{
                      color: FORTA_ORANGE,
                    }}
                  >
                    <>
                      The following recommended assessments are missing:{' '}
                      {missingRecommendedToolsText}
                    </>
                  </Typography>
                )}
              </>
            )}
            {Boolean(assessment?.id) &&
              tools.map((tool: IAssessmentTool, i: number) => (
                <Fragment key={tool.id}>
                  <Assessment assessmentTool={tool} />
                  {i < tools.length - 1 && (
                    <Divider
                      sx={{
                        height: 2,
                        backgroundColor: FORTA_BLUE.primary,
                        my: 3,
                        borderRadius: '3px',
                      }}
                    />
                  )}
                </Fragment>
              ))}
            {isAdding && (
              <>
                <Divider
                  sx={{
                    height: 2,
                    backgroundColor: FORTA_BLUE.primary,
                    my: 3,
                    borderRadius: '3px',
                  }}
                />
                <Assessment onRemove={handleDeleteNewAssessment} />
              </>
            )}
            {(isAdding || tools.length > 0) && (
              <Divider
                sx={{
                  height: 2,
                  backgroundColor: FORTA_BLUE.primary,
                  my: 3,
                  borderRadius: '3px',
                }}
              />
            )}
            {isEditable && assessment?.id && (
              <Box sx={{ display: 'flex', mt: 1 }}>
                <FHButton
                  disabled={!assessment?.id || isAdding || toolsLoading}
                  colorTheme={Themes.Primary}
                  startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
                  onClick={handleAddAssessmentTool}
                  error={tools.length === 0 && !isAdding}
                  data-testid="addAssessmentButton"
                >
                  Add Assessment
                </FHButton>
              </Box>
            )}
          </FBoxContainer>
          {Boolean(assessment?.id) && <AssessmentSummary />}
        </>
      )}
      {(assessmentLoading || toolsLoading || barriersLoading) && <FProgressBar />}
    </Box>
  );
}

export default InitialAssessment;
